import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Check, Loader2, Sparkles, Stethoscope, GraduationCap, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const FREE = ["Unlimited dog profiles", "Weight, food & exercise tracking", "Health problems & medication schedule + reminders", "Training logs & progress reports", "Community forum access"];
const PREMIUM = ["Everything in Free", "AI Vet symptom research (Gemini & GPT-5.5)", "AI pack & behaviour analysis (wolf-researcher informed)", "AI custom training-plan builder", "Enrol in professional training courses"];

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setChecking(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          await refresh();
          toast.success("You're now Premium! 🎉 All AI features unlocked.");
          setChecking(false);
          navigate("/upgrade", { replace: true });
          return;
        }
        if (r.data.status === "expired") { toast.error("Payment session expired."); setChecking(false); return; }
      } catch { /* keep polling */ }
      if (attempts < 6) setTimeout(poll, 2000);
      else { setChecking(false); toast.info("Still processing — refresh in a moment."); }
    };
    poll();
  }, [location.search]); // eslint-disable-line

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/subscribe", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch { toast.error("Could not start checkout"); setBusy(false); }
  };

  const premium = user?.is_premium;

  return (
    <div data-testid="upgrade-page" className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="h-12 w-12 rounded-2xl bg-secondary/15 flex items-center justify-center mx-auto mb-3"><Crown className="h-6 w-6 text-secondary" /></div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">{premium ? "You're Premium 🎉" : "Unlock My Pack Premium"}</h1>
        <p className="text-muted-foreground mt-2">{premium ? "Thanks for supporting My Pack. All AI tools & courses are unlocked." : "Get AI research, pack/behaviour insights, custom training plans and course access."}</p>
      </div>

      {checking && (
        <div className="flex items-center justify-center gap-2 text-muted-foreground mb-6"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment...</div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-card border border-border/70 p-6 shadow-sm">
          <div className="text-sm font-medium text-muted-foreground">Free</div>
          <div className="font-head text-3xl font-bold mt-1">$0</div>
          <ul className="space-y-2 mt-5">
            {FREE.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{f}</li>)}
          </ul>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-secondary/10 to-accent/10 border-2 border-secondary/40 p-6 shadow-sm relative">
          <span className="absolute -top-3 left-6 text-xs bg-secondary text-white px-3 py-1 rounded-full font-medium">Most popular</span>
          <div className="text-sm font-medium text-secondary flex items-center gap-1"><Crown className="h-4 w-4" /> Premium</div>
          <div className="font-head text-3xl font-bold mt-1">$2.99<span className="text-base font-normal text-muted-foreground">/month</span></div>
          <ul className="space-y-2 mt-5 mb-6">
            {PREMIUM.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Sparkles className="h-4 w-4 text-secondary shrink-0 mt-0.5" />{f}</li>)}
          </ul>
          {premium ? (
            <Button disabled className="w-full rounded-full" data-testid="already-premium">Active ✓</Button>
          ) : (
            <Button data-testid="subscribe-btn" onClick={subscribe} disabled={busy} className="w-full rounded-full bg-secondary hover:bg-secondary/90">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirecting...</> : "Go Premium"}
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground text-center mt-2">Test mode — use Stripe test card 4242 4242 4242 4242.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-8 text-center">
        {[[Stethoscope, "AI Vet Research"], [Users, "Pack & Behaviour AI"], [GraduationCap, "Course Access"]].map(([Icon, label], i) => (
          <div key={i} className="rounded-xl bg-card border border-border/60 p-4"><Icon className="h-5 w-5 mx-auto text-primary mb-2" /><div className="text-xs font-medium">{label}</div></div>
        ))}
      </div>
    </div>
  );
}
